import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Accordion } from '@sorare/core/src/atoms/layout/Accordion/index.tsx';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_SERIES_PLAY,
  ANY_SPORT_SERIES_SQUAD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { useCommonGameSet } from '@sorare/core/src/set';

import squadImg from '#src/assets/squad.webp';
import { useIsSquadEnabled } from '#src/hooks/squad/useIsSquadEnabled.ts';

import { SeriesBoardTimer } from '../SeriesBoardTimer/index.tsx';
import type {
  SeriesBoardTrckSwitcher_squad,
  SeriesBoardTrckSwitcher_tasksTrack,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  height: calc(8 * var(--unit));
  overflow: visible;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  background: rgba(var(--c-rgb-black), 0.8);
`;

const AccordionWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

type Props = {
  trck: SeriesBoardTrckSwitcher_tasksTrack;
  squad: Nullable<SeriesBoardTrckSwitcher_squad>;
  selected: 'individual' | 'squad';
};

export const SeriesBoardTrckSwitcher = ({ trck, squad, selected }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const commonGameSet = useCommonGameSet();
  const { SetIcon } = commonGameSet.visual.brand;
  const isSquadEnabled = useIsSquadEnabled();
  const [expanded, setExpanded] = useState(false);

  const individualTrack = (
    <ConditionalWrapper
      wrap={isSquadEnabled}
      Wrapper={Link}
      props={{
        to: generateSportContextPath(ANY_SPORT_SERIES_PLAY),
      }}
    >
      <Horizontal>
        <SetIcon size={5} />
        <Vertical gap={0.5}>
          <BrandS>{trck.title}</BrandS>
          {!!commonGameSet.endDate && (
            <SeriesBoardTimer endDate={commonGameSet.endDate} />
          )}
        </Vertical>
      </Horizontal>
    </ConditionalWrapper>
  );
  const squadTrack = (
    <Link to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD)}>
      <Horizontal>
        <img src={squadImg} width={40} alt="Squad Track" />
        <Vertical>
          <BrandS>
            {squad ? squad.name : <FormattedMessage {...glossary.squads} />}
          </BrandS>
        </Vertical>
      </Horizontal>
    </Link>
  );

  if (!isSquadEnabled) {
    return individualTrack;
  }

  return (
    <Root>
      {expanded ? <Backdrop /> : null}
      <AccordionWrapper>
        <Accordion
          noBorder
          noHorizontalPadding
          title={selected === 'individual' ? individualTrack : squadTrack}
          onExpandChange={setExpanded}
        >
          {selected === 'individual' ? squadTrack : individualTrack}
        </Accordion>
      </AccordionWrapper>
    </Root>
  );
};

SeriesBoardTrckSwitcher.fragments = {
  tasksTrack: gql`
    fragment SeriesBoardTrckSwitcher_tasksTrack on TasksTrack {
      id
      title
    }
  ` as TypedDocumentNode<SeriesBoardTrckSwitcher_tasksTrack>,
  squad: gql`
    fragment SeriesBoardTrckSwitcher_squad on Squad {
      id
      name
    }
  ` as TypedDocumentNode<SeriesBoardTrckSwitcher_squad>,
};
